// Utmerkelser (Awards) page - displays live achievements that members have earned
// Shows awards grouped by type and who earned them

import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { app } from './firebase-config'; // Firebase configuration
import { Link } from 'react-router-dom';

// How many names to show before collapsing behind "+N more"
const VISIBLE_COUNT = 2;

// Text explanations for each award type - describes what must be done to earn it
const awardCriteria = {
  "Beste arrangør vandrepokal👑": "Den/de som har flest vertspoeng", // Most host points
  "⚜️Festivity Specialist⚜️": "De som har arrangert minst to eventer av samme type.", // Hosted 2+ of same type
  "🥇Maestro of Ceremonies🥇": "De som har arrangert minst et event av alle typer (KL🥩, Vino🍷, KD🧠 og Blåtur🌍", // Hosted all types
  "🏅Golden Hat Trick🏅": "De som har arrangert minst et event av typene KL🥩, Vino🍷 og Blåtur🌍", // Hosted 3 specific types
  "🎗️KL Score Master🎗️": "De som har arrangert KL🥩 med rating over 3,6", // Hosted high-rated KL events
};

// Prestige rank: lower number = higher prestige (shown first). Unranked default to 99.
const prestigeOrder = {
  "👑Beste arrangør👑": 1,
  "🥇Maestro of Ceremonies🥇": 2,
  "🏅Golden Hat Trick🏅": 3,
  "⚜️Festivity Specialist⚜️": 4,
  "🎗️KL Score Master🎗️": 5,
};

// The single most prestigious award name (used for special styling)
const MOST_PRESTIGIOUS = "Beste arrangør vandrepokal👑";
const BEST_PARTICIPANT = "Beste deltaker vandrepokal🏆";

// Helper: render an array of {id, name} as comma-separated links
const UserLinks = ({ users }) => (
  <>{users.map((u, i) => (
    <React.Fragment key={u.id}>
      {i > 0 && ', '}
      <Link to={`/profil/${u.id}`} className="user-link">{u.name}</Link>
    </React.Fragment>
  ))}</>
);

// ── LiveScoreboard: shows the vandrepokaler (rolling trophies) ──
export const LiveScoreboard = () => {
  const [rows, setRows] = useState([]);
  const [expandedInfo, setExpandedInfo] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toggleInfo = (key) => setExpandedInfo(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleUsers = (key) => setExpandedUsers(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const fetchData = async () => {
      const db = getFirestore(app);
      const usersCollection = collection(db, 'users');
      try {
        const usersSnapshot = await getDocs(usersCollection);
        const usersList = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        // Row 1: Beste arrangør (highest host_points)
        const maxHostPoints = Math.max(...usersList.map(u => u.host_points || 0));
        const topHosts = usersList.filter(u => (u.host_points || 0) === maxHostPoints && maxHostPoints > 0);

        // Row 2: Beste deltaker (highest points)
        const maxPoints = Math.max(...usersList.map(u => u.points || 0));
        const topParticipants = usersList.filter(u => (u.points || 0) === maxPoints && maxPoints > 0);

        // Sync both vandrepokal awards in Firestore
        for (const user of usersList) {
          const userRef = doc(db, 'users', user.id);
          const isTopHost = topHosts.some(h => h.id === user.id);
          const hasHostAward = (user.awards || []).includes(MOST_PRESTIGIOUS);
          if (isTopHost && !hasHostAward) {
            await updateDoc(userRef, { awards: arrayUnion(MOST_PRESTIGIOUS) });
          } else if (!isTopHost && hasHostAward) {
            await updateDoc(userRef, { awards: arrayRemove(MOST_PRESTIGIOUS) });
          }

          const isTopParticipant = topParticipants.some(tp => tp.id === user.id);
          const hasParticipantAward = (user.awards || []).includes(BEST_PARTICIPANT);
          if (isTopParticipant && !hasParticipantAward) {
            await updateDoc(userRef, { awards: arrayUnion(BEST_PARTICIPANT) });
          } else if (!isTopParticipant && hasParticipantAward) {
            await updateDoc(userRef, { awards: arrayRemove(BEST_PARTICIPANT) });
          }
        }

        setRows([
          {
            name: MOST_PRESTIGIOUS,
            users: topHosts.map(u => ({ id: u.id, name: u.name })),
            criteria: `Den/de som har flest vertpoeng (nå: ${maxHostPoints} poeng)`,
          },
          {
            name: BEST_PARTICIPANT,
            users: topParticipants.map(u => ({ id: u.id, name: u.name })),
            criteria: `Den/de som har flest deltakerpoeng (nå: ${maxPoints} poeng)`,
          },
        ]);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching vandrepokaler:', err);
        setError('Error fetching vandrepokaler: ' + err.message);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <section id="live-scoreboard" className="utmerkelser-section">
      <h2>🏅Vandrepokaler🏅</h2>
      <div className="table-responsive"><table>
        <thead><tr><th>Vandrepokaler</th><th>Nåværende eiere</th></tr></thead>
        <tbody>
          {rows.map((row, i) => {
            const isUsersExpanded = expandedUsers[i];
            const visible = isUsersExpanded ? row.users : row.users.slice(0, VISIBLE_COUNT);
            const remaining = row.users.length - VISIBLE_COUNT;
            return (
              <React.Fragment key={i}>
                <tr>
                  <td>
                    <span className="award-name-clickable" onClick={() => toggleInfo(i)} title="Klikk for detaljer">
                      {row.name}
                    </span>
                    {expandedInfo[i] && (
                      <div className="award-criteria-inline">{row.criteria}</div>
                    )}
                  </td>
                  <td>
                    <span className="achievers-inline">
                      <UserLinks users={visible} />
                      {!isUsersExpanded && remaining > 0 && (
                        <button className="achievers-more-chip" onClick={() => toggleUsers(i)}>+{remaining} til</button>
                      )}
                      {isUsersExpanded && remaining > 0 && (
                        <button className="achievers-more-chip" onClick={() => toggleUsers(i)}>vis færre</button>
                      )}
                    </span>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table></div>
    </section>
  );
};

// ── LiveAchievements: self-contained component for the live awards table ──
export const LiveAchievements = () => {
  const [awards, setAwards] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAwards = async () => {
      const db = getFirestore(app);
      const usersCollection = collection(db, 'users');
      try {
        const usersSnapshot = await getDocs(usersCollection);
        const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const allAwards = usersList.flatMap(user =>
          (user.awards || []).map(awardName => ({ name: awardName, user: { id: user.id, name: user.name } }))
        );
        const groupedAwards = {};
        for (const award of allAwards) {
          if (!groupedAwards[award.name]) groupedAwards[award.name] = { users: [] };
          groupedAwards[award.name].users.push(award.user);
        }
        const awardsData = Object.entries(groupedAwards).map(([name, data]) => ({ name, users: data.users }));
        awardsData.sort((a, b) => (prestigeOrder[a.name] || 99) - (prestigeOrder[b.name] || 99));
        setAwards(awardsData.filter(a => a.name !== MOST_PRESTIGIOUS && a.name !== BEST_PARTICIPANT));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching awards:', err);
        setError('Error fetching awards: ' + err.message);
        setLoading(false);
      }
    };
    fetchAwards();
  }, []);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleUsers = (key) => setExpandedUsers(prev => ({ ...prev, [key]: !prev[key] }));

  const renderUserList = (users, key) => {
    if (!Array.isArray(users) || users.length === 0) return <em>Ingen</em>;
    const isExpanded = expandedUsers[key];
    const visible = isExpanded ? users : users.slice(0, VISIBLE_COUNT);
    const remaining = users.length - VISIBLE_COUNT;
    return (
      <span className="achievers-inline">
        <UserLinks users={visible} />
        {!isExpanded && remaining > 0 && (
          <button className="achievers-more-chip" onClick={() => toggleUsers(key)}>+{remaining} til</button>
        )}
        {isExpanded && remaining > 0 && (
          <button className="achievers-more-chip" onClick={() => toggleUsers(key)}>vis færre</button>
        )}
      </span>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <section id="live-achievements" className="utmerkelser-section">
      <h2>🎖️Utmerkelser🎖️</h2>
      <div className="table-responsive"><table>
        <thead><tr><th>Utmerkelser</th><th>Vinnere</th></tr></thead>
        <tbody>
          {awards.map((award, index) => {
            return (
              <React.Fragment key={index}>
                <tr>
                  <td>
                    <span className="award-name-clickable" onClick={() => toggleRow(index)} title="Klikk for detaljer">
                      {award.name}
                    </span>
                    {expandedRows[index] && (
                      <div className="award-criteria-inline">
                        {awardCriteria[award.name] || "Ingen spesifikk beskrivelse tilgjengelig for denne utmerkelsen."}
                      </div>
                    )}
                  </td>
                  <td>
                    {renderUserList(award.users, `award-${index}`)}
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table></div>
    </section>
  );
};

// ── SpesielleUtmerkelser: self-contained component for the special awards table ──
export const SpesielleUtmerkelser = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [userMap, setUserMap] = useState({});
  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const fetchUsers = async () => {
      const db = getFirestore(app);
      const snapshot = await getDocs(collection(db, 'users'));
      const map = {};
      snapshot.docs.forEach(d => {
        const name = d.data().name;
        if (name) map[name.toLowerCase()] = d.id;
      });
      setUserMap(map);
    };
    fetchUsers();
  }, []);

  const renderLinkedNames = (text) => {
    const parts = text.split(/ og |, /);
    return parts.map((name, i) => {
      const trimmed = name.trim();
      const userId = userMap[trimmed.toLowerCase()];
      return (
        <React.Fragment key={i}>
          {i > 0 && (text.includes(', ') ? ', ' : ' og ')}
          {userId ? <Link to={`/profil/${userId}`} className="user-link">{trimmed}</Link> : trimmed}
        </React.Fragment>
      );
    });
  };

  const specialAwards = [
    { text1: 'Beste Arrangør siden 2017👑 (2025)', text2: 'Njaal og Magnus', description: 'Har samlet flest vertpoeng siden 2017. En meget gjev hyllest!' },
    { text1: 'Host of the Most: Vino🍷 (2024)', text2: 'Gustaf', description: 'Høy kvalitet. Eneste som har arrangert 2x Vinos' },
    { text1: 'Host of the Most: KD🧠 (2024)', text2: 'Markus', description: 'Høy kvalitet. Eneste som har arrangert 2x KD' },
    { text1: 'Julegrøt🎅 hederspris (2024)', text2: 'Martin og Petter', description: 'Konsistent! Julegrøt x8 siden 2017!' },
    { text1: 'Julebord🎄 hederspris (2024)', text2: 'Nikolai', description: 'Julebord x5 siden 2017' },
    { text1: 'Host ærespris✨ (2024)', text2: 'Didrik', description: 'Kongen på kjøkkenet, alltid på tilbudssiden, ypperlig vert!' },
    { text1: '💪FitnessPT💪(2025)', text2: 'Gil og Nils', description: 'Grunnlegger av Guttas Fitnesstest' }
  ];

  return (
    <section id="spesielle-utmerkelser" className="utmerkelser-section">
      <h2>🏅Premieringer🏅</h2>
      <div className="table-responsive"><table>
        <thead><tr><th>Utgitte premieringer</th><th>Vinnere</th></tr></thead>
        <tbody>
          {specialAwards.map((row, index) => (
            <React.Fragment key={index}>
              <tr>
                <td>
                  <span className="award-name-clickable" onClick={() => toggleRow(`legacy-${index}`)} title="Klikk for begrunnelse">
                    {row.text1}
                  </span>
                  {expandedRows[`legacy-${index}`] && (
                    <div className="award-criteria-inline">{row.description}</div>
                  )}
                </td>
                <td>
                  <span className="achievers-inline">{renderLinkedNames(row.text2)}</span>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table></div>
    </section>
  );
};

// ── Combined Utmerkelser (used by Scoreboard) ──
const Utmerkelser = () => {
  const [activeFilter, setActiveFilter] = useState('alle');

  return (
    <div>
      <div className="filter-chip-bar">
        <button className={`filter-chip${activeFilter === 'alle' ? ' active' : ''}`} onClick={() => setActiveFilter('alle')}>Alle</button>
        <button className={`filter-chip${activeFilter === 'vandrepokaler' ? ' active' : ''}`} onClick={() => setActiveFilter('vandrepokaler')}>🏅 Vandrepokaler</button>
        <button className={`filter-chip${activeFilter === 'utmerkelser' ? ' active' : ''}`} onClick={() => setActiveFilter('utmerkelser')}>🎖️ Utmerkelser</button>
        <button className={`filter-chip${activeFilter === 'premieringer' ? ' active' : ''}`} onClick={() => setActiveFilter('premieringer')}>🏅 Premieringer</button>
      </div>
      {(activeFilter === 'alle' || activeFilter === 'vandrepokaler') && <LiveScoreboard />}
      {(activeFilter === 'alle' || activeFilter === 'utmerkelser') && <LiveAchievements />}
      {(activeFilter === 'alle' || activeFilter === 'premieringer') && <SpesielleUtmerkelser />}
    </div>
  );
};

export default Utmerkelser;
